/**
 * Shared WS protocol types for the Control Room (régie) feature.
 *
 * These types are consumed by:
 * - Electron hub (`web.ts`)
 * - Electron renderer and local web (Socket.IO clients)
 * - Web-client (remote regie)
 */

/**
 * Describes one publishable Control Room event extracted from a loaded layout.
 */
export interface ControlRoomCapability {
  /** Normalized shortcut combo (e.g. `"ctrl+shift+t"`). */
  combo: string;
  /** Human-readable event name. */
  name: string;
  /** Human-readable event description. */
  description: string;
}

/**
 * A target (display or widget) publishes its capabilities to the hub.
 */
export interface ControlRoomPublishPayload {
  /** Meet identifier that scopes this target to a single competition. */
  meetId: string;
  /** Opaque identifier for the target (e.g. socket id or display name). */
  targetId: string;
  /** Human-readable label for the target (e.g. "Athlete Display"). */
  targetLabel: string;
  /** All publishable events for this target. */
  capabilities: ControlRoomCapability[];
}

/**
 * Aggregated card shown in the regie UI.
 * Groups all targets that share the same combo/name/description triple.
 */
export interface ControlRoomCard {
  combo: string;
  name: string;
  description: string;
  /** Map from targetLabel to count of matching event nodes on that target. */
  targets: Record<string, number>;
}

/**
 * Full snapshot of all capabilities, sent from hub to regie.
 */
export interface ControlRoomSnapshot {
  /** Meet identifier associated with this snapshot. */
  meetId: string;
  cards: ControlRoomCard[];
}

/**
 * Payload sent from regie to hub to dispatch a shortcut.
 */
export interface ControlRoomDispatchPayload {
  /** Meet identifier associated with the dispatch request. */
  meetId: string;
  combo: string;
}

/**
 * Payload sent from hub to targets to signal a shortcut.
 */
export interface ControlRoomSignalPayload {
  /** Meet identifier associated with the emitted signal. */
  meetId: string;
  combos: string[];
}

/**
 * Minimal live meet data displayed in the remote Control Room selector.
 */
export interface ControlRoomLiveMeetSummary {
  meetId: string;
  name: string;
}

/**
 * Payload containing all live meets currently hosted for the authenticated user.
 */
export interface ControlRoomLiveMeetsPayload {
  meets: ControlRoomLiveMeetSummary[];
}

// ── WS event names ──────────────────────────────────────────────────────────

export const CR_EVENT = {
  PUBLISH_CAPABILITIES: "cr:publish-capabilities",
  REQUEST_LIVE_MEETS: "cr:request-live-meets",
  LIVE_MEETS: "cr:live-meets",
  REQUEST_SNAPSHOT: "cr:request-snapshot",
  /** APP client asks the gateway for current web-target cards only. */
  REQUEST_WEB_SNAPSHOT: "cr:request-web-snapshot",
  SNAPSHOT: "cr:snapshot",
  /** Web-target cards pushed from gateway to APP for local regie merge. */
  WEB_SNAPSHOT: "cr:web-snapshot",
  DISPATCH_SHORTCUT: "cr:dispatch-shortcut",
  SIGNAL_SHORTCUT: "cr:signal-shortcut",
} as const;

// ── Hub-side aggregation helper ─────────────────────────────────────────────

/**
 * Builds the card list from the current set of target capabilities.
 */
export function buildSnapshotCards(
  targets: Map<string, ControlRoomPublishPayload>,
  meetId?: string,
): ControlRoomCard[] {
  const byKey = new Map<string, ControlRoomCard>();

  for (const target of targets.values()) {
    if (meetId && target.meetId !== meetId) {
      continue;
    }
    for (const cap of target.capabilities) {
      const key = `${cap.combo}\0${cap.name}\0${cap.description}`;
      let card = byKey.get(key);
      if (!card) {
        card = {
          combo: cap.combo,
          name: cap.name,
          description: cap.description,
          targets: {},
        };
        byKey.set(key, card);
      }
      card.targets[target.targetLabel] =
        (card.targets[target.targetLabel] ?? 0) + 1;
    }
  }

  return [...byKey.values()];
}

/**
 * Merges multiple card groups, aggregating target counts for matching events.
 */
export function mergeControlRoomCards(
  ...groups: ControlRoomCard[][]
): ControlRoomCard[] {
  const byKey = new Map<string, ControlRoomCard>();

  for (const group of groups) {
    for (const card of group) {
      const key = `${card.combo}\0${card.name}\0${card.description}`;
      const existing = byKey.get(key);
      if (!existing) {
        byKey.set(key, {
          combo: card.combo,
          name: card.name,
          description: card.description,
          targets: { ...card.targets },
        });
        continue;
      }

      for (const [targetLabel, count] of Object.entries(card.targets)) {
        existing.targets[targetLabel] =
          (existing.targets[targetLabel] ?? 0) + count;
      }
    }
  }

  return [...byKey.values()];
}
